/**
 * @description Given a string, return the string with the first letter capitalized.
 * @param {string} string - The string that you want to have capitalized.
 * @return {string} The string with the first letter capitalized.
 * @example
 * capitalizeFirstLetter('john'); // John
 */
function capitalizeFirstLetter(string) {
	if (typeof string !== 'string') {
		throw new Error(
			'The parameter provided is not a string.  Please pass a string.'
		);
	}

	return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * @description Get the base URL for the client to construct URLs to send to users.
 * @param {string} clientVariablePrefix - The prefix for environment variables containing the protocol and domain for the client.
 * @return {string} The base URL for the client.
 */
function getBaseClientUrl(clientVariablePrefix) {
	// Node
	if (typeof window === 'undefined') {
		const protocolEnvironmentVariable =
			process.env[`${clientVariablePrefix}_PROTOCOL`];
		const domainEnvironmentVariable =
			process.env[`${clientVariablePrefix}_DOMAIN`];

		return `${protocolEnvironmentVariable}://${domainEnvironmentVariable}/`;
	}
	// Browser
	else {
		return `${window.location.origin}/`;
	}
}

/**
 * @description Given a filename string, get the file's extension.
 * @param {string} filename - The name of the from which you want to get the extension.
 * @return {string} The extension of the given filename.
 * @example
 * getFileExtension('picture.jpg'); // jpg
 */
function getFileExtension(filename) {
	const regEx = /(?:\.([^.]+))?$/;

	if (filename == null) {
		throw new Error(
			'Filename is either null or not defined.  Please pass a string.'
		);
	}

	return regEx.exec(filename)[1];
}

/**
 * @description Given a full name string, retrieve the first name.
 * @param {string} fullName - The full name from which you want to retrieve the first name.
 * @return {string} The first name taken from the full name.
 * @example
 * getFirstName('John Hammond'); // John
 */
function getFirstName(fullName) {
	if (fullName == null) {
		throw new Error(
			'Full name is either null or not defined.  Please pass a string.'
		);
	}

	if (fullName.indexOf(' ') === -1) {
		return fullName;
	}

	return fullName.substr(0, fullName.indexOf(' '));
}

/**
 * @description Given an array of elements, turn them into a comma separated list string.
 * @param {array} array - The array of elements you wish to include in the list.  The elements can be strings or numbers.
 * @param {boolean} [includeAnd = true] - Whether or not to include the word "and" before the last item in the list.
 * @return {string} The comma separated list of elements from the array.
 * @example
 * makeCommaSeparatedList(['Peter', 'Paul', 'Mary']); // Peter, Paul, and Mary
 */
function makeCommaSeparatedList(array, includeAnd = true) {
	if (!Array.isArray(array)) {
		throw new Error('Please send an array to turn into a list.');
	}

	if (array.length === 0) {
		return '';
	}

	let list;

	if (array.length === 1) {
		return array[0];
	}

	if (array.length === 2 && includeAnd) {
		return array[0] + ' and ' + array[1];
	}

	list = array.join(', ');

	if (includeAnd) {
		const lastCommaIndex = list.lastIndexOf(',');
		list =
			list.substr(0, lastCommaIndex + 1) +
			' and' +
			list.substr(lastCommaIndex + 1);
	}

	return list;
}

export { capitalizeFirstLetter, getBaseClientUrl, getFileExtension, getFirstName, makeCommaSeparatedList };
